<script setup lang="ts">
import { VForm } from 'vuetify/components/VForm'
import { useHead } from '@unhead/vue'
import authV1BottomShape from '@images/svg/auth-v1-bottom-shape.svg?raw'
import authV1TopShape from '@images/svg/auth-v1-top-shape.svg?raw'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import AuthBrandHeader from '@/components/auth/AuthBrandHeader.vue'

const { t } = useI18n()

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
    public: true,
  },
})

const title = import.meta.env.VITE_APP_RU === 'only' ? 'base_title_ru' : 'base_title'

useHead({
  title: `${t(title)} - ${t('auth.two_factor_title')}`,
})

const route = useRoute()
const router = useRouter()
const ability = useAbility()

const formInProcess = ref<boolean>(false)
const resendInProcess = ref<boolean>(false)
const resendSuccess = ref<boolean>(false)
const resendCooldown = ref<number>(0)

const twoFactorMethod = ref<string>((route.query.method as string) || 'email')

const errors = ref<Record<string, string | undefined>>({
  code: undefined,
})

const refVForm = ref<VForm>()

const form = ref({
  code: '',
})

const resetError = (name: string) => {
  errors.value[name] = undefined
}

// Cooldown timer
let cooldownInterval: ReturnType<typeof setInterval> | null = null

const startCooldown = (seconds: number) => {
  resendCooldown.value = seconds
  if (cooldownInterval) {
    clearInterval(cooldownInterval)
  }
  cooldownInterval = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0 && cooldownInterval) {
      clearInterval(cooldownInterval)
      cooldownInterval = null
    }
  }, 1000)
}

onMounted(() => {
  startCooldown(60) // Initial cooldown after page load
})

onUnmounted(() => {
  if (cooldownInterval) {
    clearInterval(cooldownInterval)
  }
})

const verifyCode = async () => {
  try {
    const res = await $api('/auth/two-factor/verify', {
      method: 'POST',
      body: {
        code: form.value.code,
      },
      headers: {
        Accept: 'application/json',
      },
      credentials: 'include',
      onResponse() {
        errors.value = {}
      },
      onResponseError({ response }) {
        const data = (response && typeof response._data === 'object') ? response._data : {}
        errors.value = (data as Record<string, any>)?.errors ?? {}
      },
    })

    const payload = (res && typeof res === 'object' && 'data' in (res as any) && (res as any).data)
      ? (res as any).data
      : (typeof res === 'object' ? (res as any) : {})

    const abilityRules = Array.isArray(payload?.userAbilityRules) ? payload.userAbilityRules : []
    const userData = (payload && typeof payload === 'object' && 'userData' in payload) ? payload.userData : null
    const organizationSettings = (payload && typeof payload === 'object' && 'organizationSettings' in payload)
      ? payload.organizationSettings
      : null

    if (!userData) {
      throw new Error('Invalid auth response shape')
    }

    useCookie('userAbilityRules').value = abilityRules
    ability.update(abilityRules)

    useCookie('userData').value = userData
    useCookie('organizationSettings').value = organizationSettings

    // Validate redirect URL to prevent open redirect attacks
    const redirectTo = route.query.to ? String(route.query.to) : '/'
    const safeRedirect = redirectTo.startsWith('/') && !redirectTo.startsWith('//') ? redirectTo : '/'
    await router.replace(safeRedirect)
  }
  catch (err) {
    formInProcess.value = false
    if (Object.keys(errors.value).length === 0)
      console.error(err)
  }
  finally {
    formInProcess.value = false
  }
}

const resendCode = async () => {
  if (resendCooldown.value > 0)
    return

  resendInProcess.value = true
  resendSuccess.value = false

  try {
    await $api('/auth/two-factor/resend', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
      },
      credentials: 'include',
    })

    resendSuccess.value = true
    startCooldown(60)
  }
  catch (err) {
    console.error(err)
  }
  finally {
    resendInProcess.value = false
  }
}

const onSubmit = () => {
  refVForm.value?.validate()
    .then(({ valid: isValid }) => {
      if (isValid) {
        formInProcess.value = true
        verifyCode()
      }
    })
}

const backToLogin = () => {
  router.push({ name: 'login' })
}

const description = computed(() => {
  return twoFactorMethod.value === 'sms'
    ? t('auth.two_factor_description_sms')
    : t('auth.two_factor_description_email')
})
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <div class="position-relative my-sm-16">
      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1TopShape })"
        class="text-primary auth-v1-top-shape d-none d-sm-block"
      />

      <VNodeRenderer
        :nodes="h('div', { innerHTML: authV1BottomShape })"
        class="text-primary auth-v1-bottom-shape d-none d-sm-block"
      />

      <VCard
        class="auth-card pa-4"
        max-width="448"
      >
        <VCardItem class="justify-center">
          <AuthBrandHeader />
        </VCardItem>

        <VCardText>
          <p class="mb-4">
            {{ description }}
          </p>

          <VAlert
            v-if="resendSuccess"
            density="compact"
            color="success"
            variant="tonal"
            class="mb-4"
          >
            {{ t('auth.two_factor_resend_success') }}
          </VAlert>

          <VForm
            ref="refVForm"
            @submit.prevent="onSubmit"
          >
            <VRow>
              <VCol cols="12">
                <AppTextField
                  v-model="form.code"
                  autofocus
                  :label="t('auth.two_factor_code_label')"
                  :placeholder="t('auth.two_factor_code_placeholder')"
                  :rules="[requiredValidator]"
                  :error-messages="errors?.code"
                  maxlength="6"
                  @update:model-value="resetError('code')"
                />
              </VCol>

              <VCol cols="12">
                <VBtn
                  block
                  :disabled="formInProcess"
                  :loading="formInProcess"
                  type="submit"
                  class="mb-4"
                >
                  {{ t('auth.two_factor_verify') }}
                </VBtn>

                <VBtn
                  block
                  variant="tonal"
                  :disabled="resendInProcess || resendCooldown > 0"
                  :loading="resendInProcess"
                  class="mb-4"
                  @click="resendCode"
                >
                  {{ resendCooldown > 0 ? `${t('auth.two_factor_resend')} (${resendCooldown}s)` : t('auth.two_factor_resend') }}
                </VBtn>

                <div class="d-flex align-center justify-center">
                  <a
                    href="javascript:;"
                    class="text-primary"
                    @click="backToLogin"
                  >
                    {{ t('auth.two_factor_back') }}
                  </a>
                </div>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </div>
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth.scss";
</style>
